import type { Emitter } from "@core/events/emitter";
import type {
  AnnotationAuthor,
  AnnotationData,
  AnnotationQuat,
  AnnotationReplyData,
  AnnotationVec3,
} from "@models/data/annotationData";

export interface IAnnotationsModel {
  readonly onChanged: Emitter<{ sceneId: string }>;
  readonly onPanelOpenChanged: Emitter<boolean>;
  readonly onVisibilityChanged: Emitter<boolean>;
  /**
   * Emitted when a cloud persist (create/update/resolve/delete/reply) fails
   * after the optimistic in-memory change was rolled back. The UI surfaces it
   * as an error toast — mirrors `ProjectModel.onSceneEntitiesLoadFailed`
   * (Campfire_Web_CloudData: don't silently swallow persistence failures).
   */
  readonly onPersistError: Emitter<{ message: string }>;
  /**
   * Emitted on a transform-only marker move (drag preview + commit), so the
   * 3D renderer repositions the marker without recomposing its bubble or
   * rebuilding the whole scene (mirrors `onMediaTileUpdated`).
   */
  readonly onAnnotationMoved: Emitter<{ sceneId: string; annotationId: string }>;
  /**
   * Emitted when a marker should open its inline text editor at the marker's
   * screen position (e.g. right after placing a new comment). The viewport
   * shows a small textarea over the bubble; committing calls `updateComment`.
   */
  readonly onEditRequested: Emitter<{ sceneId: string; annotationId: string }>;
  /**
   * Emitted when a marker is clicked and should open its context menu
   * (Reply / Edit / Resolve / Delete …) at the marker's screen position —
   * the viewport anchors `CommentContextView` over the bubble. Mirrors Unity's
   * click-to-open-annotation-actions behaviour.
   */
  readonly onContextMenuRequested: Emitter<{ sceneId: string; annotationId: string }>;

  readonly isPanelOpen: boolean;
  readonly areAnnotationsVisible: boolean;

  setPanelOpen(open: boolean): void;
  setAnnotationsVisible(visible: boolean): void;
  getAnnotations(sceneId: string): ReadonlyArray<AnnotationData>;
  createComment(sceneId: string, text: string, creator: AnnotationAuthor): AnnotationData;
  /**
   * Create a scene-placed (anchored) comment with world-space marker geometry.
   * `attachmentPosition` is the fixed surface anchor (bezier start),
   * `placementPosition` the initial marker position (bezier end / bubble).
   */
  createAnnotationAt(
    sceneId: string,
    text: string,
    creator: AnnotationAuthor,
    attachmentPosition: AnnotationVec3,
    placementPosition: AnnotationVec3,
    placementRotation?: AnnotationQuat,
  ): AnnotationData;
  /** In-memory marker move during a drag (no persist); fires onAnnotationMoved. */
  previewPlacement(
    sceneId: string,
    annotationId: string,
    placementPosition: AnnotationVec3,
    placementRotation?: AnnotationQuat,
  ): void;
  /**
   * In-memory ANCHOR move during a drag (no persist); fires onAnnotationMoved.
   * The drag persists through {@link reanchorAnnotation}, so the same frame
   * rule applies: never call it for a row with a `parentSnapshotId`.
   */
  previewAttachment(
    sceneId: string,
    annotationId: string,
    attachmentPosition: AnnotationVec3,
  ): void;
  /** Persist the final marker position (Unity `PlacementPose` flag). */
  commitPlacement(
    sceneId: string,
    annotationId: string,
    placementPosition: AnnotationVec3,
    placementRotation?: AnnotationQuat,
  ): void;
  /**
   * Move a comment's anchor AND marker and persist both (web-only: Unity
   * never moves an anchor after creation). World space, like
   * {@link createAnnotationAt} — never call it for a row with a
   * `parentSnapshotId`, whose positions are part-local.
   */
  reanchorAnnotation(
    sceneId: string,
    annotationId: string,
    attachmentPosition: AnnotationVec3,
    placementPosition: AnnotationVec3,
    placementRotation?: AnnotationQuat,
  ): void;
  /**
   * Begin a sketch annotation (Unity `BeginCreateNewAnnotation(isSketch:true)`
   * → `CreateAnnotationAsync` + `SketchUploaded=false` follow-up). The row is
   * created immediately (peers see it) but stays invalid/hidden everywhere
   * until {@link finishSketch}. Placement pose = camera pose at capture.
   */
  createSketchAnnotation(
    sceneId: string,
    creator: AnnotationAuthor,
    placementPosition: AnnotationVec3,
    placementRotation: AnnotationQuat,
    fov: number,
  ): AnnotationData;
  /** Unity `FinishSketchAsync` — flips `SketchUploaded` true (row becomes valid). */
  finishSketch(sceneId: string, annotationId: string): void;
  /**
   * Inbound `sketch_uploaded` (tc 36): a peer finished uploading a sketch's
   * PNGs — mark the row valid locally (no persist; the sender already wrote
   * the flag). No-op when the row isn't loaded (full annotation-row
   * reconcile is still deferred).
   */
  applySketchUploadedFromCloud(sketchId: string): void;
  /**
   * Inbound `create_annotation` (tc 2). Adds a peer's new annotation to the
   * in-memory index without persisting (the sender already wrote the rows).
   * Idempotent — Unity's listener deliberately loops own creates back.
   */
  applyAnnotationCreatedFromCloud(wireAnnotation: Record<string, unknown>): void;
  /**
   * Inbound `edit_annotation` (tc 2). `flags` picks the operation
   * (0 = whole annotation excluding replies, 1 = add reply, 2 = delete reply,
   * 3 = edit reply — see `ANNOTATION_UPDATE_FLAGS`); an unknown id with
   * flags 0 falls back to a create (Unity's late-joiner heal).
   */
  applyAnnotationEditedFromCloud(
    flags: number,
    replyId: string,
    wireAnnotation: Record<string, unknown>,
  ): void;
  /** Inbound `delete_annotation` (tc 10). Idempotent row + marker removal. */
  applyAnnotationDeletedFromCloud(annotationId: string): void;
  /** Unity `CancelCreateNewAnnotation` — permanently deletes the in-progress sketch row. */
  cancelSketch(sceneId: string, annotationId: string): void;
  /** Ask the viewport to open the inline text editor for a marker. */
  requestTextEdit(sceneId: string, annotationId: string): void;
  /** Ask the viewport to open the marker's context menu (Reply/Resolve/Delete…). */
  requestContextMenu(sceneId: string, annotationId: string): void;
  updateComment(sceneId: string, annotationId: string, text: string): void;
  addReply(sceneId: string, annotationId: string, message: string, creator: AnnotationAuthor): AnnotationReplyData | null;
  updateReply(sceneId: string, annotationId: string, replyId: string, message: string): void;
  deleteReply(sceneId: string, annotationId: string, replyId: string): void;
  setResolved(sceneId: string, annotationId: string, resolved: boolean): void;
  deleteComment(sceneId: string, annotationId: string): void;
}

export const ANNOTATIONS_MODEL = Symbol.for("Campfire.AnnotationsModel");
